import React, { useState } from 'react';
import { isRegistered, register as registerEntrant } from '../api/campaign';
// Campaign 2 T&C (Atlas X Innovate Consumer Promotion, Aug-Sep 2026).
import tcPdf from '../pdf/atlas-tc-2026.pdf';
// CAMPAIGN 2 IS ATLAS ONLY -- the separate per-brand T&C files are switched off.
// import pdf1 from '../pdf/innovate.pdf';
// import pdf2 from '../pdf/atlas.pdf';

export default function Register({ nextPage, b, shopId, setUserId }) {
    const [isLoading, setIsLoading] = useState(false);
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [billImage, setBillImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    // Ticked by default; the participant may untick, but then cannot continue
    // (the Start button stays disabled), since entry is deemed acceptance.
    const [acceptedTerms, setAcceptedTerms] = useState(true);
    const [showErrorMessage, setShowErrorMessage] = useState(false); // NEW
    const [isRegisterClicked, setIsRegisterClicked] = useState(false);

    // Color palette based on brand
    const colors = b === 2 ? {
        bgFrom: '#8B2500',
        bgVia: '#C4621F',
        bgTo: '#E87722',
        primary: '#F59E42',
        primaryLight: '#FFA94D',
        buttonFrom: '#F59E42',
        buttonTo: '#FFA94D',
        textAccent: '#FED7AA',
        textLight: '#FFEDD5',
        glow: 'rgba(245, 158, 66, 0.2)'
    } : {
        bgFrom: '#1A0D2E',
        bgVia: '#5D1A5E',
        bgTo: '#B81F7B',
        primary: '#EC4899',
        primaryLight: '#F472B6',
        buttonFrom: '#EC4899',
        buttonTo: '#F472B6',
        textAccent: '#F9A8D4',
        textLight: '#FBCFE8',
        glow: 'rgba(236, 72, 153, 0.2)'
    };

    // Single T&C document this campaign (was per-brand: b === 1 ? pdf1 : pdf2)
    const pdfFileName = tcPdf;

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setBillImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeImage = () => {
        setBillImage(null);
        setImagePreview(null);
    };

    const downloadTermsPDF = () => {
        // Create a link element and trigger download
        const link = document.createElement('a');
        link.href = `${pdfFileName}`;
        // download was set to the bundled path, so the file saved under its
        // hashed build name. Give it a readable one instead.
        link.download = 'Atlas-Innovate-Promotion-Terms-2026.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const register = async () => {
        if (!name || !phone || !billImage || !acceptedTerms || isRegisterClicked) return;
        setIsRegisterClicked(true);
        
        try {
            if (await isRegistered(phone)) {
                setShowErrorMessage(true);
                setIsRegisterClicked(false);
                setTimeout(() => {setShowErrorMessage(false); window.location.reload();}, 5000);
                return;
            }
        } catch (error) {
            // A failed duplicate check must not silently let a double entry
            // through, so stop here and let the user retry.
            console.log("Duplicate check failed:", error);
            setIsRegisterClicked(false);
            return;
        }

        setIsLoading(true);

        try {
            // Uploads the bill photo to the file store, then writes the row.
            const { userId } = await registerEntrant({
                name,
                phone,
                billImage,
                shopId,
                b
            });

            setUserId(userId);
            setIsLoading(false);
            nextPage(2); // Go to next page after successful save
        } catch (error) {
            console.log("Error:", error);
            setIsLoading(false); // RESET on error
            setIsRegisterClicked(false); // RESET on error
        }
    };

    return (
        <div 
            className='flex justify-center w-full min-h-screen relative overflow-hidden'
            style={{ background: `linear-gradient(to bottom right, ${colors.bgFrom}, ${colors.bgVia}, ${colors.bgTo})` }}
        >
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-20 left-10 w-32 h-32 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: colors.glow }}></div>
                <div className="absolute bottom-20 right-10 w-40 h-40 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: colors.glow, animationDelay: '1s' }}></div>
                <div className="absolute top-1/2 left-1/3 w-36 h-36 rounded-full blur-2xl animate-pulse" style={{ backgroundColor: colors.glow, animationDelay: '2s' }}></div>
            </div>

            {/* NEW - Error Message Box */}
            {showErrorMessage && (
                <div className="fixed top-8 left-1/2 transform -translate-x-1/2 z-50 animate-slide-down">
                    <div className="bg-red-500/90 backdrop-blur-md border-2 border-red-300/50 rounded-2xl px-6 py-4 shadow-2xl flex items-center gap-3 min-w-[320px]">
                        <div className="flex-shrink-0">
                            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <p className="text-white font-semibold text-base">Already Registered!</p>
                            <p className="text-white/90 text-sm">This phone number is already registered.</p>
                        </div>
                        <button 
                            onClick={() => setShowErrorMessage(false)}
                            className="flex-shrink-0 text-white/80 hover:text-white transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            <div className="relative z-10 w-full px-6 py-8 flex flex-col justify-center max-w-md">
                <div className="text-center mb-12 animate-fade-in">
                    <div className="inline-block bg-white/10 backdrop-blur-sm px-6 py-3 rounded-2xl mb-4 shadow-lg">
                        <h1 className='text-3xl uppercase font-bold text-white tracking-wider'>Registration</h1>
                    </div>
                </div>

                <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 shadow-2xl border border-white/20 animate-slide-up">
                    <form className="space-y-6">
                        <div className="space-y-3 animate-slide-up" style={{ animationDelay: '0.1s' }}>
                            <label htmlFor="name" className="block text-xl font-semibold text-white drop-shadow-lg">Full Name</label>
                            <div className="relative group">
                                <input 
                                    type="text" 
                                    id="name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white text-lg rounded-2xl focus:ring-4 focus:border-white block w-full px-5 py-4 shadow-lg placeholder:text-white/60 transition-all duration-300 group-hover:border-white/50"
                                    placeholder="Enter your name" 
                                    required 
                                />
                            </div>
                        </div>

                        <div className="space-y-3 animate-slide-up" style={{ animationDelay: '0.2s' }}>
                            <label htmlFor="phone" className="block text-xl font-semibold text-white drop-shadow-lg">Phone Number</label>
                            <div className="relative group">
                                <input 
                                    type="tel" 
                                    id="phone"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    className="bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white text-lg rounded-2xl focus:ring-4 focus:border-white block w-full px-5 py-4 shadow-lg placeholder:text-white/60 transition-all duration-300 group-hover:border-white/50" 
                                    placeholder="Enter your phone" 
                                    required 
                                />
                            </div>
                        </div>

                        <div className="space-y-3 animate-slide-up" style={{ animationDelay: '0.3s' }}>
                            <label htmlFor="billImage" className="block text-xl font-semibold text-white drop-shadow-lg">Bill Photo</label>
                            
                            {!imagePreview ? (
                                <div className="relative group">
                                    <input 
                                        type="file" 
                                        id="billImage"
                                        accept="image/*"
                                        capture="environment"
                                        onChange={handleImageChange}
                                        className="hidden"
                                        required 
                                    />
                                    <label 
                                        htmlFor="billImage"
                                        className="flex flex-col items-center justify-center bg-white/20 backdrop-blur-sm border-2 border-dashed border-white/30 text-white rounded-2xl cursor-pointer hover:bg-white/25 transition-all duration-300 py-8 px-4 group-hover:border-white/50"
                                    >
                                        <svg className="w-16 h-16 mb-3" style={{ color: colors.textAccent }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <p className="text-base font-medium mb-1">Upload Bill Photo</p>
                                        <p className="text-sm text-white/70">Tap to take photo or choose from gallery</p>
                                    </label>
                                </div>
                            ) : (
                                <div className="relative group animate-scale-in">
                                    <div className="bg-white/20 backdrop-blur-sm border-2 border-white/30 rounded-2xl overflow-hidden shadow-lg">
                                        <img 
                                            src={imagePreview} 
                                            alt="Bill preview" 
                                            className="w-full h-48 object-cover"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={removeImage}
                                        className="absolute top-3 right-3 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transition-all duration-300 transform hover:scale-110 active:scale-95"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                    <div className="mt-2 flex items-center justify-center gap-2 text-white/80 text-sm">
                                        <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        <span>Bill photo uploaded</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Terms and Conditions */}
                        <div className="space-y-3 animate-slide-up" style={{ animationDelay: '0.4s' }}>
                            <div className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                                <input
                                    type="checkbox"
                                    id="terms"
                                    checked={acceptedTerms}
                                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                                    className="mt-1 w-5 h-5 rounded cursor-pointer accent-current flex-shrink-0"
                                    style={{ accentColor: colors.primary }}
                                    required
                                />
                                <label htmlFor="terms" className="text-white text-sm leading-relaxed cursor-pointer">
                                    I agree to the{' '}
                                    <button
                                        type="button"
                                        onClick={downloadTermsPDF}
                                        className="font-semibold underline hover:opacity-80 transition-opacity inline-flex items-center gap-1"
                                        style={{ color: colors.textAccent }}
                                    >
                                        <span>Terms and Conditions</span>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    </button>
                                    {' '}of this promotion
                                </label>
                            </div>

                            {/* Unticking is allowed, but entry cannot continue without it. */}
                            {!acceptedTerms && (
                                <div className="flex items-start gap-2 rounded-2xl border border-red-300/50 bg-red-500/20 p-3">
                                    <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-100" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <p className="text-xs text-red-50">
                                        You must accept the Terms and Conditions to take part in this promotion.
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className='pt-6 animate-slide-up' style={{ animationDelay: '0.5s' }}>
                            <button 
                                onClick={register} 
                                type="button"
                                disabled={isLoading || !name || !phone || !billImage || !acceptedTerms || isRegisterClicked} // ADD isRegisterClicked
                                className="relative w-full text-white font-bold text-lg rounded-2xl px-8 py-5 shadow-2xl transition-all duration-300 transform hover:scale-105 active:scale-95 disabled:transform-none disabled:cursor-not-allowed overflow-hidden group"
                                style={{ 
                                    background: (isLoading || !name || !phone || !billImage || !acceptedTerms || isRegisterClicked) ? 'linear-gradient(to right, #9CA3AF, #6B7280)' : `linear-gradient(to right, ${colors.buttonFrom}, ${colors.buttonTo})`, // ADD isRegisterClicked
                                    boxShadow: `0 20px 50px ${colors.glow}`
                                }}
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                                {isLoading ? (
                                    <div className="flex items-center justify-center gap-2">
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span>Processing...</span>
                                    </div>
                                ) : (
                                    <span className="relative z-10">Start</span>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <style jsx>{`
                @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slide-up { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes slide-down { from { opacity: 0; transform: translate(-50%, -20px); } to { opacity: 1; transform: translate(-50%, 0); } }
                @keyframes scale-in { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
                .animate-fade-in { animation: fade-in 0.8s ease-out forwards; }
                .animate-slide-up { animation: slide-up 0.6s ease-out forwards; }
                .animate-slide-down { animation: slide-down 0.4s ease-out forwards; }
                .animate-scale-in { animation: scale-in 0.4s ease-out forwards; }
            `}</style>
        </div>
    );
}